import {
  CLINICIAN_TYPE,
  DEFAULT_APPOINTMENT_DURATION_MINUTES,
  DEFAULT_SESSION_DURATION_MINUTES,
  PRESCRIBER_SCHEDULING_WINDOW_DAYS,
  PRESCRIBER_UNDER_18_DURATION_MINUTES,
  THERAPIST_SCHEDULING_WINDOW_DAYS,
  UNDER_12_AGE_RANGES,
  UNDER_18_AGE_RANGES,
} from "@src/constants";

export interface SchedulingMetadataInput {
  ageRange?: string | null;
  clinicianType?: string | null;
}

export interface SchedulingMetadata {
  durationMinutes: number;
  sessionDurationMinutes: number;
  schedulingWindowDays: number;
}

export interface SchedulingRequestParams {
  clinicianType?: unknown;
  duration?: unknown;
}

export interface PrescriberAgeRangeDurationMatchInput {
  selectedAgeRange?: string | null;
  patientAgeRange?: string | null;
  clinicianType?: string | null;
}

export interface PrescriberAgeRangeDurationMismatch {
  isMismatch: true;
  selectedDurationMinutes: number;
  patientDurationMinutes: number;
}

export interface PrescriberAgeRangeDurationMatch {
  isMismatch: false;
}

export type PrescriberAgeRangeDurationMatchResult =
  | PrescriberAgeRangeDurationMismatch
  | PrescriberAgeRangeDurationMatch;

const VALID_CLINICIAN_TYPES = new Set<string>(Object.values(CLINICIAN_TYPE));
const VALID_DURATIONS = new Set<number>([
  DEFAULT_APPOINTMENT_DURATION_MINUTES,
  PRESCRIBER_UNDER_18_DURATION_MINUTES,
]);

export const isUnder12AgeRange = (ageRange?: string | null): boolean => {
  return Boolean(ageRange && UNDER_12_AGE_RANGES.includes(ageRange));
};

export const isUnder18AgeRange = (ageRange?: string | null): boolean => {
  return Boolean(ageRange && UNDER_18_AGE_RANGES.includes(ageRange));
};

export const getAppointmentDurationMinutes = (
  ageRange?: string | null,
  clinicianType?: string | null,
): number => {
  return clinicianType === CLINICIAN_TYPE.PRESCRIBER && isUnder18AgeRange(ageRange)
    ? PRESCRIBER_UNDER_18_DURATION_MINUTES
    : DEFAULT_APPOINTMENT_DURATION_MINUTES;
};

export const getSessionDurationMinutes = (
  ageRange?: string | null,
  clinicianType?: string | null,
): number => {
  return clinicianType === CLINICIAN_TYPE.PRESCRIBER && isUnder18AgeRange(ageRange)
    ? PRESCRIBER_UNDER_18_DURATION_MINUTES
    : DEFAULT_SESSION_DURATION_MINUTES;
};

export const getSchedulingWindowDays = (clinicianType?: string | null): number => {
  return clinicianType === CLINICIAN_TYPE.PRESCRIBER
    ? PRESCRIBER_SCHEDULING_WINDOW_DAYS
    : THERAPIST_SCHEDULING_WINDOW_DAYS;
};

export const buildSchedulingMetadata = ({
  ageRange,
  clinicianType,
}: SchedulingMetadataInput): SchedulingMetadata => {
  return {
    durationMinutes: getAppointmentDurationMinutes(ageRange, clinicianType),
    sessionDurationMinutes: getSessionDurationMinutes(ageRange, clinicianType),
    schedulingWindowDays: getSchedulingWindowDays(clinicianType),
  };
};

export const validatePrescriberAgeRangeDurationMatch = ({
  selectedAgeRange,
  patientAgeRange,
  clinicianType,
}: PrescriberAgeRangeDurationMatchInput): PrescriberAgeRangeDurationMatchResult => {
  if (!selectedAgeRange || !patientAgeRange || clinicianType !== CLINICIAN_TYPE.PRESCRIBER) {
    return { isMismatch: false };
  }

  const selectedDurationMinutes = getAppointmentDurationMinutes(
    selectedAgeRange,
    CLINICIAN_TYPE.PRESCRIBER,
  );
  const patientDurationMinutes = getAppointmentDurationMinutes(
    patientAgeRange,
    CLINICIAN_TYPE.PRESCRIBER,
  );

  if (selectedDurationMinutes === patientDurationMinutes) {
    return { isMismatch: false };
  }

  return {
    isMismatch: true,
    selectedDurationMinutes,
    patientDurationMinutes,
  };
};

export const validateSchedulingRequestParams = ({
  clinicianType,
  duration,
}: SchedulingRequestParams): string[] => {
  const errors: string[] = [];

  if (Array.isArray(clinicianType)) {
    errors.push("clinician_type must be a single value.");
  } else if (clinicianType !== undefined) {
    if (typeof clinicianType !== "string") {
      errors.push("clinician_type must be a string.");
    } else if (!VALID_CLINICIAN_TYPES.has(clinicianType)) {
      errors.push("clinician_type must be a supported clinician type.");
    }
  }

  if (Array.isArray(duration)) {
    errors.push("duration must be a single value.");
  } else if (duration !== undefined) {
    const parsedDuration = typeof duration === "number" ? duration : Number(duration);

    if (!Number.isInteger(parsedDuration) || !VALID_DURATIONS.has(parsedDuration)) {
      errors.push("duration must be a supported appointment duration.");
    }
  }

  return errors;
};
